import { SIGNUP_URL } from '@/lib/urls';

/**
 * Codici invito: il «porta un collega» (MGM) e le convenzioni con ordini e
 * associazioni. Per la landing sono la stessa cosa: un codice che arriva
 * dal link condiviso (`/invito/CODE?ch=CANALE`) o da un `?ref=CODE` su
 * qualunque pagina, si lega alla visita (traffic source) e si
 * inoltra alla registrazione. Il tipo lo dice il backend quando si chiede il
 * codice (`GET /v1/invite-codes/{code}`).
 */

/** Canali ammessi dal backend: un valore fuori lista diventa null, non «link». */
export const INVITE_CHANNELS = [
  'email',
  'linkedin',
  'facebook',
  'whatsapp',
  'instagram',
  'link',
] as const;

export type InviteChannel = (typeof INVITE_CHANNELS)[number];

export interface InviteParams {
  code: string;
  /** null = non sappiamo da dove arriva */
  channel: InviteChannel | null;
}

/**
 * Pulizia minima, la stessa del backend: maiuscole, via spazi e trattini.
 * Il backend normalizza comunque; qui serve a non mandargli spazzatura e a
 * usare una sola chiave di cache per `k7pm-3qrx` e `K7PM3QRX`.
 */
export function normalizeInviteCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.replace(/[\s-]+/g, '').toUpperCase();
  return /^[A-Z0-9]{4,64}$/.test(code) ? code : null;
}

export function normalizeInviteChannel(raw: string | null | undefined): InviteChannel | null {
  const channel = raw?.trim().toLowerCase();
  return INVITE_CHANNELS.includes(channel as InviteChannel) ? (channel as InviteChannel) : null;
}

/**
 * Il codice di /invito/{CODE}: il link che il backend fa condividere
 * (`shareLink`). Sta nel percorso e non nella query perché LinkedIn, al clic
 * sull'anteprima, la query la toglie: il canale (`?ch=`) può perdersi, il
 * codice no.
 */
const INVITE_PATH = /^\/invito\/([^/]+)\/?$/;

/**
 * L'invito dell'indirizzo: il codice dal percorso /invito/{CODE}, altrimenti
 * da `?ref=` (su qualunque pagina: link scritti a mano, vecchia forma); il
 * canale sempre da `?ch=`. null se il codice manca o non ha la forma giusta.
 */
export function readInviteParams(pathname: string, search: string): InviteParams | null {
  const params = new URLSearchParams(search);
  let fromPath: string | null = null;
  try {
    fromPath = decodeURIComponent(pathname.match(INVITE_PATH)?.[1] ?? '') || null;
  } catch {
    // Percorso malformato: vale come se il codice non ci fosse
  }
  const code = normalizeInviteCode(fromPath) ?? normalizeInviteCode(params.get('ref'));
  if (!code) return null;
  return { code, channel: normalizeInviteChannel(params.get('ch')) };
}

/**
 * La registrazione con l'invito appeso. Il backend attribuisce già dal
 * cookie della visita; i parametri servono all'app per passare il codice
 * esplicito alla creazione dell'azienda, e reggono anche quando il cookie
 * non c'è (init fallito, cookie bloccati).
 */
export function signupUrlWithInvite(invite: InviteParams | null): string {
  if (!invite) return SIGNUP_URL;
  const params = new URLSearchParams({ ref: invite.code });
  if (invite.channel) params.set('ch', invite.channel);
  return `${SIGNUP_URL}?${params.toString()}`;
}

/**
 * L'unico segmento che l'export genera per /invito/{CODE} e
 * /convenzioni/{slug}: i file (`out/invito/_.html`, `out/convenzioni/_.html`)
 * che la function CloudFront serve a ogni codice e a ogni slug. Quello vero
 * lo legge la pagina dal browser. Come codice non passa la normalizzazione.
 */
export const PLACEHOLDER_SEGMENT = '_';
